import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import api from '../../services/api';

const VideoForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { fetchVideos, showToast } = useApp();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');
    const [videoSourceType, setVideoSourceType] = useState('url');
    const [thumbnailSourceType, setThumbnailSourceType] = useState('url');
    const [videoFile, setVideoFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

    // Generate slug from title
    const generateSlugFromTitle = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    // Handle title change with auto-slug generation
    const handleTitleChange = (e) => {
        const newTitle = e.target.value;
        setFormData({ ...formData, title: newTitle });

        if (!slugManuallyEdited) {
            const newSlug = generateSlugFromTitle(newTitle);
            setFormData(prev => ({ ...prev, slug: newSlug }));
        }
    };

    // Handle manual slug edit
    const handleSlugChange = (e) => {
        const newSlug = e.target.value
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');

        setFormData({ ...formData, slug: newSlug });
        setSlugManuallyEdited(true);
    };

    // Auto-generate slug
    const autoGenerateSlug = () => {
        const newSlug = generateSlugFromTitle(formData.title);
        setFormData({ ...formData, slug: newSlug });
        setSlugManuallyEdited(false);
        showToast('✅ Slug auto-generated from title');
    };

    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        description: '',
        url: '',
        thumb: '',
        thumbData: '',
        category: 'News',
        dur: '',
        status: 'live',
        featured: 'no',
        date: new Date().toISOString().split('T')[0],
        seo: {
            metaTitle: '',
            metaDescription: '',
            metaKeywords: '',
            canonicalUrl: '',
            ogTitle: '',
            ogDescription: '',
            ogImage: '',
            twitterTitle: '',
            twitterDescription: '',
            twitterImage: '',
            focusKeyword: ''
        },
        videoSEO: {
            transcript: '',
            captions: '',
            chapterMarkers: [],
            videoDuration: '',
            embedUrl: ''
        }
    });

    useEffect(() => {
        if (id) {
            fetchVideo();
        }
    }, [id]);

    const fetchVideo = async () => {
        try {
            const res = await api.get(`/videos/${id}`);
            setFormData(res.data);
            if (res.data.slug) {
                setSlugManuallyEdited(true);
            }
            if (res.data.url && !res.data.url.startsWith('/uploads')) {
                setVideoSourceType('url');
            } else if (res.data.url && res.data.url.startsWith('/uploads')) {
                setVideoSourceType('local');
            }

            if (res.data.thumbData) {
                setThumbnailSourceType('local');
                setThumbnailPreview(res.data.thumbData);
            } else if (res.data.thumb) {
                setThumbnailSourceType('url');
                setThumbnailPreview(res.data.thumb);
            }
        } catch (error) {
            console.error('Error fetching video:', error);
            showToast('Error fetching video', 'error');
        }
    };

    const autoGenerateSEO = () => {
        const metaTitle = formData.seo.metaTitle || formData.title;
        const metaDescription = formData.seo.metaDescription || formData.description?.substring(0, 160);
        const keywords = formData.category;

        setFormData({
            ...formData,
            seo: {
                ...formData.seo,
                metaTitle: metaTitle,
                metaDescription: metaDescription,
                metaKeywords: keywords,
                ogTitle: metaTitle,
                ogDescription: metaDescription,
                twitterTitle: metaTitle,
                twitterDescription: metaDescription,
                ogImage: formData.thumb || thumbnailPreview,
                twitterImage: formData.thumb || thumbnailPreview
            }
        });
        showToast('✅ SEO fields auto-generated from video data!');
    };

    const calculateSEOScore = () => {
        let score = 0;
        if (formData.seo.metaTitle && formData.seo.metaTitle.length >= 30 && formData.seo.metaTitle.length <= 60) score += 15;
        if (formData.seo.metaDescription && formData.seo.metaDescription.length >= 120 && formData.seo.metaDescription.length <= 160) score += 15;
        if (formData.seo.metaKeywords) score += 10;
        if (formData.seo.focusKeyword && (formData.title.includes(formData.seo.focusKeyword) || formData.description?.includes(formData.seo.focusKeyword))) score += 15;
        if (formData.description && formData.description.length > 100) score += 10;
        if (formData.videoSEO.transcript && formData.videoSEO.transcript.length > 200) score += 15;
        if (formData.thumb || formData.thumbData) score += 10;
        if (formData.dur) score += 10;
        return score;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSEOChange = (e) => {
        setFormData({
            ...formData,
            seo: { ...formData.seo, [e.target.name]: e.target.value }
        });
    };

    const handleVideoSEOChange = (e) => {
        setFormData({
            ...formData,
            videoSEO: { ...formData.videoSEO, [e.target.name]: e.target.value }
        });
    };

    const handleVideoFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 100 * 1024 * 1024) {
                showToast('Video file too large. Max 100MB', 'error');
                return;
            }
            setVideoFile(file);
            const videoURL = URL.createObjectURL(file);
            setFormData({ ...formData, url: videoURL });
            showToast(`Selected: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
        }
    };

    const handleThumbnailFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showToast('Thumbnail too large. Max 5MB', 'error');
                return;
            }
            setThumbnailFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setThumbnailPreview(reader.result);
                setFormData({ ...formData, thumbData: reader.result, thumb: '' });
            };
            reader.readAsDataURL(file);
        }
    };

    const extractYouTubeInfo = () => {
        const url = formData.url;
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        if (match) {
            const videoId = match[1];
            const thumbUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
            const embedUrl = `https://www.youtube.com/embed/${videoId}`;

            setThumbnailPreview(thumbUrl);
            setFormData({
                ...formData,
                thumb: thumbUrl,
                videoSEO: { ...formData.videoSEO, embedUrl: embedUrl }
            });
            setThumbnailSourceType('url');
            showToast('✅ YouTube info extracted!');
        } else {
            showToast('❌ Invalid YouTube URL', 'error');
        }
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title) {
            showToast('Please enter a video title', 'error');
            return;
        }

        if (videoSourceType === 'url' && !formData.url) {
            showToast('Please enter a video URL', 'error');
            return;
        }

        setLoading(true);
        setUploadProgress(0);

        try {
            let submitData = { ...formData };

            if (videoFile) {
                setUploadProgress(30);
                const base64Video = await convertToBase64(videoFile);
                submitData.url = base64Video;
                setUploadProgress(60);
            }

            setUploadProgress(80);

            if (id) {
                await api.put(`/videos/${id}`, submitData);
                showToast('✅ Video updated successfully!');
            } else {
                await api.post('/videos', submitData);
                showToast('✅ Video created successfully!');
            }

            setUploadProgress(100);
            await fetchVideos();
            navigate('/admin/videos');
        } catch (error) {
            showToast('Error saving video: ' + (error.response?.data?.message || error.message), 'error');
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    return (
        <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
            <h3>{id ? '✏️ Edit Video' : '➕ Add New Video'}</h3>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', gap: '10px', borderBottom: '2px solid #eee', marginBottom: '20px', marginTop: '15px' }}>
                <button
                    type="button"
                    onClick={() => setActiveTab('basic')}
                    style={{
                        padding: '10px 20px',
                        background: activeTab === 'basic' ? 'var(--red)' : 'transparent',
                        color: activeTab === 'basic' ? '#fff' : '#666',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '5px 5px 0 0',
                        fontWeight: '600'
                    }}
                >
                    🎬 Basic Info
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('seo')}
                    style={{
                        padding: '10px 20px',
                        background: activeTab === 'seo' ? 'var(--red)' : 'transparent',
                        color: activeTab === 'seo' ? '#fff' : '#666',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '5px 5px 0 0',
                        fontWeight: '600'
                    }}
                >
                    🔍 SEO Settings
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('transcript')}
                    style={{
                        padding: '10px 20px',
                        background: activeTab === 'transcript' ? 'var(--red)' : 'transparent',
                        color: activeTab === 'transcript' ? '#fff' : '#666',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '5px 5px 0 0',
                        fontWeight: '600'
                    }}
                >
                    📝 Transcript & SEO
                </button>
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
                <div style={{ marginBottom: '20px', padding: '10px', background: '#f0f0f0', borderRadius: '5px' }}>
                    <div style={{ fontSize: '12px', marginBottom: '5px' }}>Uploading: {uploadProgress}%</div>
                    <div style={{ height: '5px', background: '#ddd', borderRadius: '5px', overflow: 'hidden' }}>
                        <div style={{ width: `${uploadProgress}%`, height: '100%', background: 'var(--red)', transition: 'width 0.3s' }}></div>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                {/* BASIC INFO TAB */}
                {activeTab === 'basic' && (
                    <div>
                        {/* Video Title */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>🎬 Video Title *</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleTitleChange}
                                required
                                style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                placeholder="Enter video title..."
                            />
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                {formData.title.length} characters (Recommended: 50-60 for SEO)
                            </div>
                        </div>

                        {/* Slug Field */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>🔗 SEO URL Slug</label>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <input
                                        type="text"
                                        name="slug"
                                        value={formData.slug || ''}
                                        onChange={handleSlugChange}
                                        style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                        placeholder="auto-generated-from-title"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={autoGenerateSlug}
                                    className="btn btn-outline"
                                    style={{ padding: '5px 15px', whiteSpace: 'nowrap' }}
                                >
                                    🔄 Auto-Generate
                                </button>
                            </div>
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                💡 SEO-friendly URL: domain.com/video/<strong>{formData.slug || 'your-url-slug'}</strong>
                            </div>
                        </div>

                        {/* Video Description */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>📝 Video Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="4"
                                style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px', fontFamily: 'inherit', resize: 'vertical' }}
                                placeholder="Enter a detailed description of the video..."
                            />
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                {formData.description.length} characters (Recommended: 150-300 for SEO)
                            </div>
                        </div>

                        {/* Video Source */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>📹 Video Source *</label>
                            <div style={{ display: 'flex', gap: '20px', marginBottom: '15px', flexWrap: 'wrap' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="radio" checked={videoSourceType === 'url'} onChange={() => setVideoSourceType('url')} />
                                    🔗 URL Link (YouTube, Vimeo, etc.)
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="radio" checked={videoSourceType === 'local'} onChange={() => setVideoSourceType('local')} />
                                    📁 Upload Video File (MP4, WebM)
                                </label>
                            </div>

                            {videoSourceType === 'url' ? (
                                <div>
                                    <input
                                        type="url"
                                        name="url"
                                        value={formData.url}
                                        onChange={handleChange}
                                        style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                        placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                                    />
                                    {formData.url && formData.url.includes('youtube') && (
                                        <button type="button" onClick={extractYouTubeInfo} style={{ marginTop: '10px', padding: '5px 15px', background: '#ff0000', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                                            🎬 Extract Video Info
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <input type="file" onChange={handleVideoFileChange} accept="video/mp4,video/webm,video/ogg" style={{ width: '100%', padding: '8px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                                    {videoFile && <p style={{ fontSize: '12px', color: 'green', marginTop: '5px' }}>✅ Selected: {videoFile.name}</p>}
                                </div>
                            )}
                        </div>

                        {/* Thumbnail */}
                        <div style={{ marginBottom: '20px', border: '1.5px solid #eee', padding: '15px', borderRadius: '8px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>🖼️ Thumbnail Image</label>
                            <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="radio" checked={thumbnailSourceType === 'url'} onChange={() => setThumbnailSourceType('url')} />
                                    🔗 Image URL
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="radio" checked={thumbnailSourceType === 'local'} onChange={() => setThumbnailSourceType('local')} />
                                    📁 Upload Image
                                </label>
                            </div>

                            {thumbnailSourceType === 'url' ? (
                                <input type="url" name="thumb" value={formData.thumb} onChange={(e) => { handleChange(e); setThumbnailPreview(e.target.value); }} style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }} placeholder="https://example.com/thumbnail.jpg" />
                            ) : (
                                <input type="file" onChange={handleThumbnailFileChange} accept="image/jpeg,image/png,image/webp,image/gif" style={{ width: '100%', padding: '8px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                            )}

                            {thumbnailPreview && (
                                <div style={{ marginTop: '15px' }}>
                                    <img src={thumbnailPreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '120px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #ddd' }} />
                                </div>
                            )}
                        </div>

                        {/* Video Details Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                                <label>📂 Category</label>
                                <select name="category" value={formData.category} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }}>
                                    <option value="News">📰 News</option>
                                    <option value="Politics">🏛 Politics</option>
                                    <option value="Sports">🏏 Sports</option>
                                    <option value="Entertainment">🎬 Entertainment</option>
                                    <option value="Business">💼 Business</option>
                                    <option value="Technology">💻 Technology</option>
                                    <option value="Viral">🔥 Viral</option>
                                    <option value="Lifestyle">🌟 Lifestyle</option>
                                </select>
                            </div>
                            <div>
                                <label>⏱️ Duration</label>
                                <input type="text" name="dur" value={formData.dur} onChange={handleChange} placeholder="e.g., 5:32" style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                            </div>
                            <div>
                                <label>📌 Status</label>
                                <select name="status" value={formData.status} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }}>
                                    <option value="live">✅ Live</option>
                                    <option value="draft">📝 Draft</option>
                                </select>
                            </div>
                            <div>
                                <label>⭐ Featured</label>
                                <select name="featured" value={formData.featured} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }}>
                                    <option value="no">No</option>
                                    <option value="yes">⭐ Yes</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                {/* SEO TAB */}
                {activeTab === 'seo' && (
                    <div>
                        <div style={{ background: '#f0f8ff', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                <div>
                                    <strong>📊 Video SEO Score: {calculateSEOScore()}%</strong>
                                    <div style={{ fontSize: '12px', marginTop: '5px' }}>
                                        {calculateSEOScore() >= 80 ? '✅ Excellent!' :
                                            calculateSEOScore() >= 60 ? '⚠️ Good!' :
                                                '🔴 Needs improvement'}
                                    </div>
                                </div>
                                <button type="button" onClick={autoGenerateSEO} className="btn btn-navy" style={{ padding: '5px 15px' }}>
                                    🤖 Auto-Generate SEO
                                </button>
                            </div>
                            <div style={{ height: '6px', background: '#ddd', borderRadius: '3px', marginTop: '10px', overflow: 'hidden' }}>
                                <div style={{ width: `${calculateSEOScore()}%`, height: '100%', background: calculateSEOScore() >= 80 ? 'green' : calculateSEOScore() >= 60 ? 'orange' : 'red' }}></div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>🎯 Focus Keyword</label>
                                <input type="text" name="focusKeyword" value={formData.seo.focusKeyword} onChange={handleSEOChange} style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                                <div style={{ fontSize: '11px', marginTop: '4px' }}>
                                    {formData.title.includes(formData.seo.focusKeyword) || formData.description?.includes(formData.seo.focusKeyword) ?
                                        '✅ Keyword found in title/description' : '⚠️ Add keyword to title or description'}
                                </div>
                            </div>

                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>🔖 Meta Title</label>
                                <input type="text" name="metaTitle" value={formData.seo.metaTitle} onChange={handleSEOChange} style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                                <div style={{ fontSize: '11px', marginTop: '4px' }}>{formData.seo.metaTitle.length} characters (Recommended: 50-60)</div>
                            </div>

                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>📝 Meta Description</label>
                                <textarea name="metaDescription" value={formData.seo.metaDescription} onChange={handleSEOChange} rows="3" style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }}></textarea>
                                <div style={{ fontSize: '11px', marginTop: '4px' }}>{formData.seo.metaDescription.length} characters (Recommended: 150-160)</div>
                            </div>

                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>🔑 Meta Keywords</label>
                                <input type="text" name="metaKeywords" value={formData.seo.metaKeywords} onChange={handleSEOChange} style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }} placeholder="keyword1, keyword2, keyword3" />
                            </div>

                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>📘 Open Graph / Social Media Title</label>
                                <input type="text" name="ogTitle" value={formData.seo.ogTitle} onChange={handleSEOChange} style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                            </div>

                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>📘 Open Graph Description</label>
                                <textarea name="ogDescription" value={formData.seo.ogDescription} onChange={handleSEOChange} rows="2" style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }}></textarea>
                            </div>
                        </div>
                    </div>
                )}

                {/* TRANSCRIPT & ADVANCED SEO TAB */}
                {activeTab === 'transcript' && (
                    <div>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>📝 Video Transcript</label>
                            <textarea
                                name="transcript"
                                value={formData.videoSEO.transcript}
                                onChange={handleVideoSEOChange}
                                rows="10"
                                style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px', fontFamily: 'monospace', fontSize: '13px' }}
                                placeholder="Paste your video transcript here... This greatly improves SEO and accessibility!"
                            />
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                {formData.videoSEO.transcript.length} characters - Transcripts help search engines understand your video content
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>🔗 Embed URL (for sharing)</label>
                            <input
                                type="url"
                                name="embedUrl"
                                value={formData.videoSEO.embedUrl}
                                onChange={handleVideoSEOChange}
                                style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                placeholder="https://www.youtube.com/embed/..."
                            />
                        </div>

                        <div className="fg full">
                            <label>🔗 Canonical URL</label>
                            <input type="url" name="canonicalUrl" value={formData.seo.canonicalUrl} onChange={handleSEOChange} style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                        </div>

                        <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px', marginTop: '20px' }}>
                            <h4>💡 SEO Tips for Videos:</h4>
                            <ul style={{ fontSize: '13px', marginTop: '10px', paddingLeft: '20px' }}>
                                <li>✅ Add a detailed transcript - helps search engines index your content</li>
                                <li>✅ Use keywords naturally in title and description</li>
                                <li>✅ Add custom thumbnail to increase click-through rate</li>
                                <li>✅ Include video duration for better user expectation</li>
                                <li>✅ Use relevant categories for better discovery</li>
                                <li>✅ Add timestamps/chapters for longer videos</li>
                            </ul>
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                    <button type="submit" className="btn btn-red" disabled={loading}>
                        {loading ? '⏳ Saving...' : (id ? '💾 Update Video' : '💾 Create Video')}
                    </button>
                    <button type="button" className="btn btn-outline" onClick={() => navigate('/admin/videos')}>Cancel</button>
                </div>
            </form>
        </div>
    );
};

export default VideoForm;